import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { PasswordInput, Popover, InputField, Button } from '@ets/design-system';

import { navigateToSignIn } from 'router/history';
import { Logger, AuthService } from '_common/services';
import type { paths } from '_types/authority';

import { PasswordCheck, LoadingPage } from '_common/components';
import SecondaryAuthPageContainer from 'Auth/components/SecondaryAuthPageContainer/SecondaryAuthPageContainer';
import { toast } from 'utils/toast';

export const TEST_IDS = {
  INPUT: {
    NEW_PASSWORD: 'newPassword',
    CONFIRM_PASSWORD: 'confirmPassword',
  },
  BUTTON: 'changePassword',
};

const ResetPage = () => {
  const intl = useIntl();
  const { hash } = useParams<RouterParams['resetPage']>();

  const [submitting, setSubmitting] = useState(false);
  const [rules, setRules] = useState<PasswordValidators>([]);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [confirmedPassword, setConfirmedPassword] = useState('');
  const [validations, setValidations] = useState({
    newPassword: '',
    confirmedPassword: '',
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    (async function getRecoverValidators() {
      try {
        const response = await new AuthService({ errorsExpected: [400, 404] }).getRecoverValidators(
          hash,
        );

        //@ts-expect-error Endpoint type issue: /api/authority/account/password/recover/{hash}/validators should also return 'validators'
        setRules(response.data.validators);
        setLoading(false);
      } catch (error) {
        if (AuthService.isAxiosError(error)) {
          if (error?.response?.status === 400) {
            const errors = error.response.data.errors[0];
            const key = errors.errors[0];
            if (errors.field === 'hash' && key === 'invalid') {
              goToSignIn();
            }
          } else if (error?.response?.status === 404) {
            setRules([]);
            setLoading(false);
          } else {
            goToSignIn();
          }
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (!rules || rules.length === 0) {
      setDisabled(false);
    }
  }, [rules]);

  const VALUES_FOR_FEEDBACK = {
    username: 'settings.user.username',
    first_name: 'settings.name.firstName',
    last_name: 'settings.name.lastName',
    email_address: 'editor.templatePreview.properties.authors.email',
  };

  const handleSetDisabled = (isDisabled: boolean) => {
    if (isDisabled) {
      setDisabled(true);
    } else if (disabled === true) {
      setDisabled(false);
      setValidations({ ...validations, newPassword: '' });
    }
  };

  const handleInputChange = (value: string, name: 'newPassword' | 'confirmedPassword') => {
    const newValidations = { ...validations };
    if (name === 'newPassword') {
      setNewPassword(value);
      if (value !== confirmedPassword && confirmedPassword !== '') {
        newValidations.confirmedPassword = intl.formatMessage({
          id: 'ERROR_PASSWORDS_DO_NOT_MATCH',
        });
      } else {
        newValidations.confirmedPassword = '';
      }
    } else {
      setConfirmedPassword(value);
      if (value === newPassword) {
        newValidations.confirmedPassword = '';
      } else if (value !== newPassword) {
        newValidations.confirmedPassword = intl.formatMessage({
          id: 'ERROR_PASSWORDS_DO_NOT_MATCH',
        });
      }
    }
    if (value === '') {
      newValidations[name] = intl.formatMessage({ id: 'ERROR_REQUIRED_INPUT' });
    } else {
      newValidations[name] = '';
    }
    setValidations(newValidations);
  };

  const goToSignIn = () => {
    navigateToSignIn();
  };

  const handleChangePassword = async () => {
    if (!disabled && newPassword === confirmedPassword) {
      setSubmitting(true);

      try {
        await new AuthService({ errorsExpected: [400] }).changePassword(hash, {
          new: newPassword,
        });

        const message = 'auth.notifications.changePassword.success.desc';
        toast({
          variant: 'success',
          title: message,
        });
        setSubmitting(false);
      } catch (error) {
        if (AuthService.isAxiosError(error)) {
          if (error?.response?.status === 400) {
            const data = error.response
              .data as paths['/api/authority/account/password/recover/{hash}']['post']['responses']['400']['content']['application/json'];
            if (data.errors) {
              const errors = data.errors[0];
              const key = errors.errors[0];
              let msg = '';
              switch (key) {
                //@ts-expect-error Endpoint type issue: /api/authority/account/password/recover/{hash} Error:400 should include 'password_too_similar'
                case 'password_too_similar':
                  msg = intl.formatMessage(
                    //@ts-expect-error Endpoint type issue: /api/authority/account/password/recover/{hash} Error:400 should include 'password_too_similiar'
                    { id: key.toUpperCase() },
                    { name: VALUES_FOR_FEEDBACK[errors[key]] },
                  );
                  break;
                case 'invalid':
                  msg = intl.formatMessage({ id: 'PASSWORD_ALREADY_UPDATED' });
                  break;
                //@ts-expect-error Endpoint type issue: /api/authority/account/password/recover/{hash} Error:400 should include 'expired'
                case 'expired':
                  msg = intl.formatMessage({ id: 'RESET_PASSWORD_EXPIRED' });
                  break;
                //@ts-expect-error Endpoint type issue: /api/authority/account/password/recover/{hash} Error:400 should include 'password_in_history'
                case 'password_in_history':
                  msg = intl.formatMessage({ id: 'PASSWORD_RECENTLY_USED' });
                  break;
                //@ts-expect-error Endpoint type issue: /api/authority/account/password/recover/{hash} Error:400 should include 'password_too_common'
                case 'password_too_common':
                  msg = intl.formatMessage({ id: 'PASSWORD_TOO_COMMON' });
                  break;
                default:
                  msg = intl.formatMessage({ id: 'ERROR_SCREEN_HEADER' });
                  break;
              }
              toast({
                variant: 'error',
                title: msg,
              });
            }

            setSubmitting(false);
          } else {
            Logger.captureException(error.response);
            const message = 'auth.errors.error';
            toast({
              variant: 'error',
              title: message,
            });
            setSubmitting(false);
          }
        }
      }
    }
  };

  return loading ? (
    <LoadingPage />
  ) : (
    <SecondaryAuthPageContainer title="CHANGE_PASSWORD" description="CHANGE_PASSWORD_DESCRIPTION">
      <InputField
        size="l"
        label={intl.formatMessage({ id: 'NEW_PASSWORD' })}
        feedbackContent={validations.newPassword}
        testId="new-password"
        style={{ textAlign: 'left' }}
      >
        <Popover.Root
          placement="left-start"
          open={isOpen}
          onOpenChange={() => {
            if (rules.length !== 0) setIsOpen(!isOpen);
          }}
          options={{
            offset: {
              crossAxis: 5,
              mainAxis: 30,
            },
          }}
          modal={true}
        >
          <Popover.Trigger asChild>
            <PasswordInput
              name="newPassword"
              hasError={!!validations.newPassword}
              value={newPassword}
              onChange={(e) => handleInputChange(e.target.value, 'newPassword')}
              autoComplete="new-password"
              placeholder={intl.formatMessage({ id: 'NEW_PASSWORD_PLACEHOLDER' })}
              size="l"
              onClick={() => {
                if (rules.length !== 0) setIsOpen(true);
              }}
              onBlur={(e) => {
                if (disabled) {
                  if (newPassword !== '' && e.target.value !== '') {
                    setValidations({
                      ...validations,
                      newPassword: intl.formatMessage({ id: 'ERROR_PASSWORD_POLICY' }),
                    });
                  }
                }
              }}
              testId={TEST_IDS.INPUT.NEW_PASSWORD}
            />
          </Popover.Trigger>
          <Popover.Content testId="password-checker">
            <PasswordCheck rules={rules} password={newPassword} setDisabled={handleSetDisabled} />
          </Popover.Content>
        </Popover.Root>
      </InputField>
      <InputField
        size="l"
        label={intl.formatMessage({ id: 'CONFIRM_PASSWORD' })}
        feedbackContent={validations.confirmedPassword}
        style={{ textAlign: 'left' }}
        testId="confirm-password"
      >
        <PasswordInput
          name="confirmedPassword"
          size="l"
          value={confirmedPassword}
          onChange={(e) => handleInputChange(e.target.value, 'confirmedPassword')}
          placeholder={intl.formatMessage({ id: 'CONFIRM_PASSWORD_PLACEHOLDER' })}
          hasError={!!validations.confirmedPassword}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleChangePassword();
            }
          }}
          testId={TEST_IDS.INPUT.CONFIRM_PASSWORD}
        />
      </InputField>
      <Button
        variant="primary"
        fullWidth
        onClick={handleChangePassword}
        loading={submitting}
        disabled={newPassword === '' || disabled || newPassword !== confirmedPassword}
        size="l"
        testId={TEST_IDS.BUTTON}
        style={{ margin: '1rem 0 2rem' }}
      >
        <FormattedMessage id="CHANGE_PASSWORD" />
      </Button>

      <Button
        size="m"
        variant="link"
        onClick={goToSignIn}
        disabled={submitting}
        testId="sign-in-to-doDOC"
      >
        <FormattedMessage id="SIGN_IN_TO_DODOC" />
      </Button>
    </SecondaryAuthPageContainer>
  );
};

export default ResetPage;
